import {create, StateCreator} from 'zustand';
import {devtools} from "zustand/middleware";
import {ToastType} from "patient-ping-remedy/packages/toast";

export interface Alert {
  content: string;
  type: ToastType;
  id?: string;
}

export type AlertState = {
  alerts: Alert[];
  setAlerts: (val: Alert[]) => void;
  addAlert: (alert: Alert) => void;
}

const AUTO_DISMISS_DELAY = 5000;
const timeoutIds = new Map<string, NodeJS.Timeout>();

const generateAlertId = () => `alert_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const alertStoreCreator: StateCreator<AlertState, [['zustand/devtools', never]]> = (set, get) => ({
  alerts: [],
  setAlerts: (val: Alert[]) => {
    const currentAlerts = get().alerts;
    currentAlerts.forEach((alert) => {
      if (!val.some((newAlert) => newAlert.id === alert.id)) {
        const timeoutId = timeoutIds.get(alert.id!);
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutIds.delete(alert.id!);
        }
      }
    });
    set({alerts: val });
  },
  addAlert: (alert: Alert) => set((state) => {
    const alertWithId = { ...alert, id: alert.id || generateAlertId() };
    const newAlerts = [...state.alerts, alertWithId];

    if (alertWithId.type === ToastType.SUCCESS) {
      const timeoutId = setTimeout(() => {
        set((currentState) => {
          const filtered = currentState.alerts.filter((a) => a.id !== alertWithId.id);
          timeoutIds.delete(alertWithId.id!);
          return { alerts: filtered };
        });
      }, AUTO_DISMISS_DELAY);

      timeoutIds.set(alertWithId.id, timeoutId);
    }

    return { alerts: newAlerts };
  }),
});

export const useAlertStore = create<AlertState>()(
  devtools(alertStoreCreator, {
    name: 'alertStore',
    enabled: ['local', 'dev', 'qa'].includes(process.env.REACT_APP_ENV || 'prod'),
  })
);
